import React, { useState, useEffect, useRef, useCallback } from 'react';
import useSWR from 'swr';
import { ExternalLink, HelpCircle, Check, Cloud, Copy } from 'lucide-react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import TabPageLayout from './TabPageLayout';
import apiV2, { orgApi } from '../utils/apiV2';
import GoogleAdsSetupGuide from './GoogleAdsSetupGuide';

const fetcher = async () => {
  const response = await orgApi.getOrganization();
  return response.data;
};

// Soft, borderless card surface that matches the Branding/Settings aesthetic.
const Section = ({ icon, title, description, children }) => (
  <div className="card-soft p-5 sm:p-6">
    <div className="flex items-start gap-3 mb-5">
      <div className="w-9 h-9 rounded-lg bg-gray-50 flex items-center justify-center shrink-0">
        {icon}
      </div>
      <div>
        <h3 className="settings-section-title">{title}</h3>
        {description && <p className="settings-section-subtitle">{description}</p>}
      </div>
    </div>
    {children}
  </div>
);

const GoogleAdsIcon = (
  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none">
    <path d="M12.87 15.07l-2.54-2.51.03-.03A17.52 17.52 0 0014.07 6H17V4h-7V2H8v2H1v1.99h11.17C11.5 7.92 10.44 9.75 9 11.35 8.07 10.32 7.3 9.19 6.69 8h-2c.73 1.63 1.73 3.17 2.98 4.56l-5.09 5.02L4 19l5-5 3.11 3.11.76-2.04zM18.5 10h-2L12 22h2l1.12-3h4.75L21 22h2l-4.5-12zm-2.62 7l1.62-4.33L19.12 17h-3.24z" fill="#4285F4" />
  </svg>
);

const GA4Icon = (
  <svg className="w-5 h-5" viewBox="0 0 192 192" fill="none">
    <path d="M96 16C64 16 32 48 32 80c0 24 16 44 40 52-4-8-6-16-6-24 0-20 14-38 30-50 0 20 10 36 24 46 4-10 6-20 6-32 8 12 14 26 14 42 0 12-4 24-10 34 24-8 40-28 40-52 0-48-40-80-74-80z" fill="#F57C00" />
    <path d="M96 96c-12 8-20 22-20 38 0 20 16 36 36 36s36-16 36-36c0-28-24-50-52-38z" fill="#FF9800" />
  </svg>
);

const BfIcon = (
  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="12" r="10" fill="#2563EB" />
    <circle cx="12" cy="12" r="4" fill="#fff" />
    <circle cx="18" cy="6" r="2.5" fill="#60A5FA" />
  </svg>
);

const BfBeaconSection = ({ orgId }) => {
  const [status, setStatus] = useState(null);
  const [checking, setChecking] = useState(false);
  const [copied, setCopied] = useState(false);

  const check = useCallback(async () => {
    if (!orgId) return;
    setChecking(true);
    try {
      const r = await apiV2.get(`/organizations/${orgId}/intelligence/beacon-status`);
      setStatus(r.data);
    } catch { /* non-blocking */ } finally {
      setChecking(false);
    }
  }, [orgId]);
  useEffect(() => { check(); }, [check]);

  const copy = () => {
    navigator.clipboard.writeText(orgId || '').then(() => {
      setCopied(true);
      toast.success('Beacon ID copied');
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <Section
      icon={BfIcon}
      title="BookingFlows Beacon"
      description="Our own website analytics — pageviews, referrers, and how visits turn into bookings"
    >
      <div className="space-y-4">
        <div>
          <Label htmlFor="bfBeaconId" className="field-label">Beacon ID</Label>
          <div className="relative mt-1">
            <Input
              id="bfBeaconId"
              value={orgId || ''}
              readOnly
              className="font-mono pr-9 text-gray-600 bg-gray-50/60"
              data-testid="bf-beacon-id-input"
            />
            <button
              onClick={copy}
              data-testid="bf-beacon-copy"
              className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 flex items-center justify-center transition-colors"
            >
              {copied ? <Check className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5" />}
            </button>
          </div>
        </div>

        <div className="flex items-center gap-2.5" data-testid="bf-beacon-status">
          {status?.firingRecently ? (
            <>
              <span className="relative flex h-1.5 w-1.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-500" />
              </span>
              <span className="text-xs text-gray-500">Live · {status.totalPageviews?.toLocaleString()} pageviews</span>
            </>
          ) : status?.installed ? (
            <>
              <span className="inline-flex rounded-full h-1.5 w-1.5 bg-gray-300" />
              <span className="text-xs text-gray-500">Installed · quiet for 48h</span>
            </>
          ) : (
            <>
              <span className="inline-flex rounded-full h-1.5 w-1.5 ring-1 ring-gray-300 bg-transparent" />
              <span className="text-xs text-gray-400">{checking ? 'Checking…' : 'Not installed yet'}</span>
            </>
          )}
          <button
            onClick={check}
            disabled={checking}
            data-testid="bf-beacon-recheck"
            className="text-[11px] text-gray-400 hover:text-gray-600 transition-colors"
          >
            re-check
          </button>
        </div>
      </div>

      <a
        href="/intelligence/website-analytics"
        className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700 mt-4"
        data-testid="bf-beacon-helm-link"
      >
        Install &amp; view analytics in HELM Intelligence
        <ExternalLink className="w-3.5 h-3.5" />
      </a>
    </Section>
  );
};

const MetaIcon = (
  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" fill="#1877F2" />
  </svg>
);

const ConversionsTab = () => {
  const [showGuide, setShowGuide] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const saveTimer = useRef(null);
  const [settings, setSettings] = useState({
    googleAdsId: '',
    googleAdsLabel: '',
    metaPixelId: '',
    ga4MeasurementId: '',
  });
  // Always-fresh snapshot so a debounced save never sends a stale sibling value
  // when several fields are edited in quick succession.
  const settingsRef = useRef(settings);

  const { data: orgData, isLoading, mutate } = useSWR(
    'organization-settings',
    fetcher,
    { revalidateOnFocus: true, dedupingInterval: 30000 }
  );

  useEffect(() => {
    if (orgData) {
      const next = {
        googleAdsId: orgData.googleAdsId || orgData.google_ads_id || '',
        googleAdsLabel: orgData.googleAdsLabel || orgData.google_ads_label || '',
        metaPixelId: orgData.metaPixelId || orgData.meta_pixel_id || '',
        ga4MeasurementId: orgData.ga4MeasurementId || orgData.google_analytics_id || '',
      };
      setSettings(next);
      settingsRef.current = next;
    }
  }, [orgData]);

  // --- Auto-save (mirrors BrandingTab) -----------------------------------
  const persist = useCallback(async () => {
    const next = settingsRef.current;
    // Auto-prefix AW- on the Google Ads conversion ID if they forgot it.
    let adsId = (next.googleAdsId || '').trim();
    if (adsId && !adsId.startsWith('AW-')) adsId = `AW-${adsId}`;

    try {
      await orgApi.updateOrganization({
        googleAdsId: adsId || null,
        googleAdsLabel: next.googleAdsLabel.trim() || null,
        metaPixelId: next.metaPixelId.trim() || null,
        ga4MeasurementId: next.ga4MeasurementId.trim() || null,
      });
      // Reflect the normalized ads ID back into the field (cache-only, no refetch
      // so we never reset an input the user might still be editing in another field).
      if (adsId !== next.googleAdsId) {
        const normalized = { ...next, googleAdsId: adsId };
        settingsRef.current = normalized;
        setSettings(normalized);
      }
      mutate({ ...(orgData || {}), googleAdsId: adsId, googleAdsLabel: next.googleAdsLabel, metaPixelId: next.metaPixelId, ga4MeasurementId: next.ga4MeasurementId }, false);
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 1600);
    } catch (error) {
      console.error('Error saving conversion settings:', error);
      toast.error('Failed to save changes');
    }
  }, [orgData, mutate]);

  const scheduleSave = useCallback(() => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => persist(), 700);
  }, [persist]);

  const handleChange = (field, value) => {
    const sanitized = value.replace(/[^a-zA-Z0-9\-_]/g, '');
    const next = { ...settingsRef.current, [field]: sanitized };
    settingsRef.current = next;
    setSettings(next);
    scheduleSave();
  };

  if (showGuide) {
    return <GoogleAdsSetupGuide onBack={() => setShowGuide(false)} />;
  }

  return (
    <TabPageLayout
      title="Conversions"
      subtitle="Connect your ad pixels and analytics so bookings are tracked end-to-end"
      loading={isLoading}
      actions={
        <div
          data-testid="conversions-autosave-status"
          className={`hidden sm:inline-flex items-center gap-1.5 text-sm font-medium transition-colors ${
            savedFlash ? 'text-green-600' : 'text-gray-400'
          }`}
        >
          {savedFlash ? <Check className="w-4 h-4" /> : <Cloud className="w-4 h-4" />}
          {savedFlash ? 'Saved' : 'Changes save automatically'}
        </div>
      }
    >
      <div className="max-w-xl space-y-5">
        {/* BookingFlows Beacon */}
        <BfBeaconSection orgId={orgData?.id || localStorage.getItem('organizationId')} />

        {/* Google Ads */}
        <Section
          icon={GoogleAdsIcon}
          title="Google Ads"
          description="Attribute bookings back to your Google Ads campaigns"
        >
          <div className="space-y-4">
            <div>
              <Label htmlFor="googleAdsId" className="field-label">Conversion ID</Label>
              <Input
                id="googleAdsId"
                value={settings.googleAdsId}
                onChange={(e) => handleChange('googleAdsId', e.target.value)}
                placeholder="AW-123456789"
                className="font-mono mt-1"
                maxLength={20}
                data-testid="google-ads-id-input"
              />
            </div>
            <div>
              <Label htmlFor="googleAdsLabel" className="field-label">Conversion Label</Label>
              <Input
                id="googleAdsLabel"
                value={settings.googleAdsLabel}
                onChange={(e) => handleChange('googleAdsLabel', e.target.value)}
                placeholder="AbCdEfGhIjKlMnOp"
                className="font-mono mt-1"
                maxLength={30}
                data-testid="google-ads-label-input"
              />
            </div>
          </div>

          <button
            onClick={() => setShowGuide(true)}
            className="inline-flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-700 font-medium mt-3"
            data-testid="open-google-ads-guide"
          >
            <HelpCircle className="w-4 h-4" />
            How to find your Conversion ID &amp; Label
          </button>
        </Section>

        {/* GA4 */}
        <Section
          icon={GA4Icon}
          title="Google Analytics 4"
          description="Send booking events to your own GA4 property"
        >
          <div>
            <Label htmlFor="ga4MeasurementId" className="field-label">Measurement ID</Label>
            <Input
              id="ga4MeasurementId"
              value={settings.ga4MeasurementId}
              onChange={(e) => handleChange('ga4MeasurementId', e.target.value)}
              placeholder="G-XXXXXXXXXX"
              className="font-mono mt-1"
              maxLength={20}
              data-testid="ga4-measurement-id-input"
            />
          </div>

          <a
            href="https://support.google.com/analytics/answer/9304153"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 mt-3"
          >
            <ExternalLink className="w-3 h-3" />
            How to find your Measurement ID
          </a>
        </Section>

        {/* Meta Pixel */}
        <Section
          icon={MetaIcon}
          title="Meta Pixel"
          description="Track conversions for Facebook &amp; Instagram ads"
        >
          <div>
            <Label htmlFor="metaPixelId" className="field-label">Pixel ID</Label>
            <Input
              id="metaPixelId"
              value={settings.metaPixelId}
              onChange={(e) => handleChange('metaPixelId', e.target.value)}
              placeholder="1234567890123456"
              className="font-mono mt-1"
              maxLength={20}
              data-testid="meta-pixel-id-input"
            />
          </div>

          <a
            href="https://www.facebook.com/business/help/952192354843755"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 mt-3"
          >
            <ExternalLink className="w-3 h-3" />
            Find your Pixel ID
          </a>
        </Section>

        {/* Test Setup */}
        <div className="card-soft p-5 sm:p-6 space-y-4">
          <div>
            <p className="settings-section-title">Verify your setup</p>
            <p className="settings-section-subtitle">
              Complete a test booking, then use these tools to confirm events are firing correctly.
            </p>
          </div>

          {/* BookingFlows Beacon */}
          <div className="space-y-2">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">BookingFlows Beacon</p>
            <div className="flex flex-wrap gap-2">
              <a
                href="/bf-beacon-tender.zip"
                download
                data-testid="bf-beacon-checker-download"
                className="inline-flex items-center gap-1.5 px-3 py-2 bg-white rounded-lg text-sm text-gray-700 ring-1 ring-gray-200 hover:bg-gray-50 transition-colors"
              >
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" fill="#2563EB" />
                  <circle cx="12" cy="12" r="4" fill="#fff" />
                  <circle cx="18" cy="6" r="2.5" fill="#60A5FA" />
                </svg>
                BF Beacon Tender
                <span className="text-xs text-gray-400">Chrome ext — unzip &amp; load unpacked</span>
              </a>
            </div>
          </div>

          {/* Google Ads + GA4 */}
          <div className="space-y-2">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Google Ads &amp; GA4</p>
            <div className="flex flex-wrap gap-2">
              <a
                href="https://tagassistant.google.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-3 py-2 bg-white rounded-lg text-sm text-gray-700 ring-1 ring-gray-200 hover:bg-gray-50 transition-colors"
              >
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="#4285F4">
                  <path d="M12.87 15.07l-2.54-2.51.03-.03A17.52 17.52 0 0014.07 6H17V4h-7V2H8v2H1v1.99h11.17C11.5 7.92 10.44 9.75 9 11.35 8.07 10.32 7.3 9.19 6.69 8h-2c.73 1.63 1.73 3.17 2.98 4.56l-5.09 5.02L4 19l5-5 3.11 3.11.76-2.04zM18.5 10h-2L12 22h2l1.12-3h4.75L21 22h2l-4.5-12zm-2.62 7l1.62-4.33L19.12 17h-3.24z" />
                </svg>
                Google Tag Assistant
                <span className="text-xs text-gray-400">Chrome ext</span>
              </a>
              <a
                href="https://analytics.google.com/analytics/web/#/p/debugview"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-3 py-2 bg-white rounded-lg text-sm text-gray-700 ring-1 ring-gray-200 hover:bg-gray-50 transition-colors"
              >
                <svg className="w-4 h-4" viewBox="0 0 192 192" fill="none">
                  <path d="M96 16C64 16 32 48 32 80c0 24 16 44 40 52-4-8-6-16-6-24 0-20 14-38 30-50 0 20 10 36 24 46 4-10 6-20 6-32 8 12 14 26 14 42 0 12-4 24-10 34 24-8 40-28 40-52 0-48-40-80-74-80z" fill="#F57C00" />
                  <path d="M96 96c-12 8-20 22-20 38 0 20 16 36 36 36s36-16 36-36c0-28-24-50-52-38z" fill="#FF9800" />
                </svg>
                GA4 DebugView
                <span className="text-xs text-gray-400">real-time events</span>
              </a>
            </div>
          </div>

          {/* Meta */}
          <div className="space-y-2">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Meta Pixel</p>
            <div className="flex flex-wrap gap-2">
              <a
                href="https://chrome.google.com/webstore/detail/meta-pixel-helper/fdgfkebogiimcoedlicjlajpkdmockpc"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-3 py-2 bg-white rounded-lg text-sm text-gray-700 ring-1 ring-gray-200 hover:bg-gray-50 transition-colors"
              >
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="#1877F2">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                </svg>
                Meta Pixel Helper
                <span className="text-xs text-gray-400">Chrome ext</span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </TabPageLayout>
  );
};

export default ConversionsTab;
